export type ReturnSettlementKind = 'REFUND' | 'STORE_CREDIT' | 'DEFLECTION';

export type ReturnSettlementActionStateInput = {
  kind: ReturnSettlementKind;
  returnId?: string | null;
  storeId?: string | null;
  reviewRequired?: boolean | null;
  ruleFlags?: unknown[] | null;
  settlementFailureReason?: string | null;
  settlementStatus?: string | null;
  settling?: boolean;
  readyLabel?: string;
  retryLabel?: string;
  processingLabel?: string;
  verificationLabel?: string;
  settlementName?: string;
};

export type ReturnSettlementActionState = {
  disabled: boolean;
  hasUnresolvedReviewSignals: boolean;
  instruction: string | null;
  isBusy: boolean;
  isOutcomeUnknown: boolean;
  isProcessing: boolean;
  isRetryable: boolean;
  kind: ReturnSettlementKind;
  label: string;
};

const SETTLEMENT_LABELS = {
  REFUND: {
    ready: 'Process and Refund',
    processing: 'Refund in progress',
    retry: 'Retry Refund',
    verification: 'Refund needs verification',
  },
  STORE_CREDIT: {
    ready: 'Resolve (Store Credit Bonus)',
    processing: 'Store credit in progress',
    retry: 'Retry Store Credit',
    verification: 'Store credit needs verification',
  },
  DEFLECTION: {
    ready: 'Issue incentive',
    processing: 'Incentive in progress',
    retry: 'Retry Incentive',
    verification: 'Incentive needs verification',
  },
} as const;

export const getSettlementActionState = (
  input: ReturnSettlementActionStateInput,
): ReturnSettlementActionState => {
  const settlementStatus = String(input.settlementStatus || '')
    .trim()
    .toUpperCase();
  const isProcessing = settlementStatus === 'PROCESSING';
  const isOutcomeUnknown = settlementStatus === 'OUTCOME_UNKNOWN';
  const isRetryable =
    settlementStatus === 'FAILED' ||
    (input.kind === 'DEFLECTION' && settlementStatus === 'DEFLECTION_FAILED');
  const isBusy = !isOutcomeUnknown && (isProcessing || Boolean(input.settling));
  const hasRequiredIdentifiers = Boolean(input.returnId && input.storeId);

  // A failed settlement also sets reviewRequired. Returned rule flags have the
  // canonical Resolve action, so they block settlement while the boolean alone
  // must not dead-end an otherwise retryable failure.
  const hasUnresolvedReviewSignals = Boolean(
    (input.ruleFlags || []).filter(Boolean).length,
  );

  const disabled =
    !hasRequiredIdentifiers ||
    hasUnresolvedReviewSignals ||
    isBusy ||
    isOutcomeUnknown;
  const defaultLabels = SETTLEMENT_LABELS[input.kind];
  const labels = {
    ready: input.readyLabel || defaultLabels.ready,
    retry: input.retryLabel || defaultLabels.retry,
    processing: input.processingLabel || defaultLabels.processing,
    verification: input.verificationLabel || defaultLabels.verification,
  };
  const label = isOutcomeUnknown
    ? labels.verification
    : isBusy
    ? labels.processing
    : isRetryable
    ? labels.retry
    : labels.ready;
  const settlementName =
    input.settlementName ||
    (input.kind === 'REFUND'
      ? 'refund'
      : input.kind === 'STORE_CREDIT'
      ? 'store credit'
      : 'incentive');
  const settlementFailureReason = String(
    input.settlementFailureReason || '',
  ).trim();
  const failureReasonPrefix = settlementFailureReason
    ? `${settlementFailureReason}${
        /[.!?]$/.test(settlementFailureReason) ? '' : '.'
      } `
    : '';

  const instruction = !hasRequiredIdentifiers
    ? 'Return details are incomplete. Refresh the page before trying again.'
    : isOutcomeUnknown
    ? `${failureReasonPrefix}Open the Shopify order and verify whether the ${settlementName} completed. Do not retry this action. After verifying the Shopify outcome, contact ShipAid support to reconcile this return.`
    : hasUnresolvedReviewSignals
    ? 'Resolve all review signals below before processing this settlement.'
    : isProcessing
    ? `This ${settlementName} is already processing. Wait for it to finish before trying again.`
    : null;

  return {
    disabled,
    hasUnresolvedReviewSignals,
    instruction,
    isBusy,
    isOutcomeUnknown,
    isProcessing,
    isRetryable,
    kind: input.kind,
    label,
  };
};
